import React from 'react';
import { clsx } from 'clsx';

export interface SimpleInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  containerClassName?: string;
}

/**
 * SimpleInput - Input component without Formik dependency
 */
export function SimpleInput({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  containerClassName,
  className,
  required,
  disabled,
  id = '',
  ...props
}: SimpleInputProps) {
  const hasError = !!error;
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={clsx('w-full', containerClassName)}>
      {label && (
        <label
          htmlFor={inputId}
          className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        {leftIcon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span className="h-5 w-5 text-gray-400 dark:text-gray-500">{leftIcon}</span>
          </div>
        )}

        <input
          id={inputId}
          {...props}
          disabled={disabled}
          className={clsx(
            'block w-full rounded-lg border transition-colors duration-200',
            'px-4 py-2.5 text-base',
            'text-gray-900 dark:text-white',
            'placeholder-gray-400 dark:placeholder-gray-500',
            'bg-white dark:bg-gray-800',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            leftIcon && 'pl-10',
            rightIcon && 'pr-10',
            hasError
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600',
            disabled && 'cursor-not-allowed bg-gray-50 opacity-50 dark:bg-gray-900',
            className
          )}
        />

        {rightIcon && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <span className="h-5 w-5 text-gray-400 dark:text-gray-500">{rightIcon}</span>
          </div>
        )}
      </div>

      {hasError && <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {helperText && !hasError && (
        <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
    </div>
  );
}
